// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// SfuCallSession is the client half of the server-side SFU: ONE PeerConnection to the
// homeserver, which forwards every other participant's media to us (and ours to them).
// Mirrors GroupCallSession's events, so the UI is identical to the mesh path; the
// difference is one upstream instead of N-1. The signalling is "server-offers": we
// send our publish offer, the SFU answers, and the SFU sends fresh offers (which we
// answer) as it adds other participants' tracks. Membership/leave is taken from the
// call-state probe (m.call.members); the SFU just carries media, keyed by user ID.

import type { GroupCallEvents, GroupCall } from "./groupcall";
import { encode } from "./whiteboard/protocol";
import type { WireOp } from "./whiteboard/types";
import { applyQualityPreset, pcQuality, type CallQuality, type VideoQuality } from "./rtc";
import { BackgroundEffect, type BgMode } from "./bgeffect";
import { enumerateInputs } from "./devices";

// 3 spatial layers (≈720p/360p/180p) the SFU can forward per-subscriber. Shared by the
// join-time publish and the pre-negotiated transceiver so both send identical RIDs.
const SIMULCAST_ENCODINGS: RTCRtpEncodingParameters[] = [
  { rid: "f", maxBitrate: 1_700_000 },
  { rid: "h", maxBitrate: 500_000, scaleResolutionDownBy: 2 },
  { rid: "q", maxBitrate: 150_000, scaleResolutionDownBy: 4 },
];

type IceServer = { urls: string[]; username?: string; credential?: string };
interface SfuContent {
  kind: "offer" | "answer" | "candidates" | "leave";
  sdp?: string;
  candidates?: RTCIceCandidateInit[];
}

export class SfuCallSession implements GroupCall {
  simulcast = false; // publish 3 video layers (f/h/q) — set from /config before joining
  private videoTransceiver: RTCRtpTransceiver | null = null; // simulcast: pre-negotiated so camera-on is a replaceTrack
  private pc: RTCPeerConnection | null = null;
  private local: MediaStream | null = null;
  private roomID = "";
  private active = false;
  private makingOffer = false;
  private screenTrack: MediaStreamTrack | null = null; // active screen-share track (in the video sender)
  private savedCamera: MediaStreamTrack | null = null; // camera set aside while screen-sharing
  private quality: VideoQuality = "auto"; // outgoing-video cap, re-applied after track changes
  private bgEffect: BackgroundEffect | null = null; // client-side background blur processor
  private bgMode: BgMode = "none";
  private rawCamera: MediaStreamTrack | null = null;

  constructor(
    private readonly sendSignal: (roomID: string, content: SfuContent) => void,
    private readonly getIceServers: () => Promise<IceServer[]>,
    private readonly myUserID: string,
    private readonly events: GroupCallEvents,
    // sendWhiteboard relays an encoded whiteboard op over the /sync socket (WB-005);
    // SFU mode has no peer mesh, so ops go server-relayed. Defaults to a no-op.
    private readonly sendWhiteboard: (roomID: string, content: unknown) => void = () => {},
  ) {}

  get isActive(): boolean {
    return this.active;
  }
  get sendingVideo(): boolean {
    return !!this.local?.getVideoTracks().length;
  }

  // join connects to the SFU. reuseStream (call transfer make-before-break): clone an
  // existing call's tracks instead of re-capturing the mic/camera — a camera can't be
  // opened twice, and cloning keeps the source alive after the old leg stops its tracks.
  async join(roomID: string, video: boolean, reuseStream?: MediaStream): Promise<void> {
    if (this.active) return;
    this.roomID = roomID;
    this.active = true;
    if (reuseStream) {
      // Reuse the SAME tracks (call transfer), NOT clones: a clone dies when the old
      // leg stops the original track. The handing-off CallSession (handOffAndHangup)
      // closes its connection WITHOUT stopping these tracks, so the SFU keeps them live.
      this.local = new MediaStream(reuseStream.getTracks());
    } else {
      if (!navigator.mediaDevices?.getUserMedia) {
        this.active = false;
        throw new Error(globalThis.isSecureContext ? "no-mic" : "insecure-context");
      }
      this.local = await navigator.mediaDevices.getUserMedia({ audio: true, video });
    }
    this.events.onLocalStream(this.local);
    await this.buildPc();
  }

  // reconnect rebuilds the SFU PeerConnection after the /sync socket dropped — on
  // disconnect the server tears down our server-side SFU peer (SFU.LeaveAll), so a bare
  // socket reconnect would leave us in a frozen call. We rebuild the peer (reusing the
  // live local tracks — no re-prompt) and re-publish; the SFU re-forwards us to others.
  async reconnect(): Promise<void> {
    if (!this.active || !this.local) return;
    await this.buildPc();
  }

  // buildPc creates the RTCPeerConnection, wires its handlers, and adds our local tracks
  // (which triggers onnegotiationneeded → a publish offer). Shared by join + reconnect.
  private async buildPc(): Promise<void> {
    const old = this.pc;
    if (old) {
      old.onconnectionstatechange = null; // don't let closing the stale pc hang up the call
      old.close();
    }
    const pc = new RTCPeerConnection({ iceServers: await this.getIceServers() });
    this.pc = pc;
    // Forwarded tracks arrive grouped per publisher (stream id = their user ID).
    pc.ontrack = (e) => {
      const uid = e.streams[0]?.id ?? "";
      if (uid && uid !== this.myUserID) this.events.onPeerStream(uid, e.streams[0]);
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) this.sendSignal(this.roomID, { kind: "candidates", candidates: [e.candidate.toJSON()] });
    };
    pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true;
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        this.sendSignal(this.roomID, { kind: "offer", sdp: offer.sdp });
      } finally {
        this.makingOffer = false;
      }
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "failed" || s === "closed") this.hangup();
    };
    let publishedVideo = false;
    if (this.local) {
      for (const t of this.local.getTracks()) {
        if (t.kind === "video") {
          this.publishVideo(t);
          publishedVideo = true;
        } else {
          pc.addTrack(t, this.local);
        }
      }
    }
    // Simulcast: pre-negotiate the sendonly video transceiver even before the camera is on,
    // so a mid-call camera-on is a reliable replaceTrack — not a fragile mid-call
    // addTransceiver + renegotiation (the P0 finding). The RIDs are negotiated up front.
    if (this.simulcast && !publishedVideo) {
      this.videoTransceiver = pc.addTransceiver("video", { direction: "sendonly", sendEncodings: SIMULCAST_ENCODINGS });
    }
    void this.applyQuality(); // keep the chosen cap across a reconnect / fresh pc
  }

  // publishVideo adds our camera/screen to the PeerConnection. With simulcast on it sends
  // 3 spatial layers (f/h/q) so the SFU can forward a per-subscriber resolution; otherwise
  // a single track. Used at join (buildPc) AND on a mid-call camera-on (upgradeToVideo).
  private publishVideo(track: MediaStreamTrack): void {
    if (!this.pc || !this.local) return;
    if (this.simulcast) {
      this.videoTransceiver = this.pc.addTransceiver(track, { direction: "sendonly", streams: [this.local], sendEncodings: SIMULCAST_ENCODINGS });
    } else {
      this.pc.addTrack(track, this.local);
    }
  }

  // onSignal handles the SFU's frames (its answer to our offer, its renegotiation
  // offers as others join, and ICE).
  async onSignal(raw: unknown): Promise<void> {
    const c = raw as SfuContent;
    if (!c || !this.pc) return;
    switch (c.kind) {
      case "answer":
        await this.pc.setRemoteDescription({ type: "answer", sdp: c.sdp }).catch(() => undefined);
        return;
      case "offer": {
        // Perfect-negotiation (we're the polite peer): if our own offer collides with
        // the SFU's, roll ours back and take theirs.
        const collision = this.makingOffer || this.pc.signalingState !== "stable";
        if (collision) await this.pc.setLocalDescription({ type: "rollback" }).catch(() => undefined);
        await this.pc.setRemoteDescription({ type: "offer", sdp: c.sdp });
        const ans = await this.pc.createAnswer();
        await this.pc.setLocalDescription(ans);
        this.sendSignal(this.roomID, { kind: "answer", sdp: ans.sdp });
        return;
      }
      case "candidates":
        for (const cand of c.candidates ?? []) await this.pc.addIceCandidate(cand).catch(() => undefined);
        return;
    }
  }

  setMuted(muted: boolean): void {
    this.local?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }
  setCameraOff(off: boolean): void {
    this.local?.getVideoTracks().forEach((t) => (t.enabled = !off));
  }

  // peerQuality grades the upstream link to the SFU. In an SFU all remote media
  // arrives over the single server connection, so every participant reflects the
  // same upstream health (per-publisher stats are the SFU quality-stats follow-up).
  peerQuality(_userID: string): Promise<CallQuality> {
    return pcQuality(this.pc);
  }

  // sendOp: SFU mode has no peer mesh, so whiteboard ops go over the server-relayed
  // m.whiteboard ephemeral frame (WB-005). The receiver decodes + de-dupes; the server
  // sees stroke geometry in SFU mode (E2E gap closed by WB-020).
  sendOp(op: WireOp): void {
    if (this.active && this.roomID) this.sendWhiteboard(this.roomID, encode(op));
  }

  async upgradeToVideo(): Promise<void> {
    if (!this.pc || this.sendingVideo || !this.local) return; // guard on what WE send, not call video
    const cam = await navigator.mediaDevices.getUserMedia({ video: true });
    const track = cam.getVideoTracks()[0];
    if (!track) return;
    this.local.addTrack(track);
    if (this.simulcast && this.videoTransceiver) {
      // The simulcast transceiver was pre-negotiated at join → just feed it the camera. No
      // renegotiation, so the RIDs flow reliably (vs the flaky mid-call addTransceiver path).
      await this.videoTransceiver.sender.replaceTrack(track);
    } else {
      this.publishVideo(track); // non-simulcast: addTrack → onnegotiationneeded re-offers
    }
    this.events.onLocalStream(this.local);
    void this.applyQuality();
  }

  get isSharingScreen(): boolean {
    return !!this.screenTrack;
  }
  get videoQuality(): VideoQuality {
    return this.quality;
  }
  get backgroundMode(): BgMode {
    return this.bgMode;
  }
  // videoEncodingCount reports how many encodings the video sender has — 3 when simulcast is
  // publishing (f/h/q), 1 otherwise. DEV/e2e only.
  videoEncodingCount(): number {
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === "video");
    return sender?.getParameters().encodings?.length ?? 0;
  }

  // setBackground turns the client-side background blur on/off (composited frames are sent,
  // so the SFU/peers never see the raw background — stays E2E).
  async setBackground(mode: BgMode): Promise<void> {
    if (mode === this.bgMode) return;
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === "video");
    if (!sender?.track || !this.local) {
      this.bgMode = mode;
      return;
    }
    if (mode === "none") {
      if (this.rawCamera) {
        await sender.replaceTrack(this.rawCamera);
        this.swapLocalVideo(this.rawCamera);
        this.rawCamera = null;
      }
      this.bgEffect?.stop();
      this.bgMode = "none";
      return;
    }
    if (this.bgEffect && this.rawCamera) {
      this.bgEffect.setMode(mode);
      this.bgMode = mode;
      return;
    }
    this.rawCamera = sender.track;
    this.bgEffect ??= new BackgroundEffect();
    const processed = await this.bgEffect.start(new MediaStream([this.rawCamera]), mode);
    const pTrack = processed.getVideoTracks()[0];
    if (!pTrack) return;
    await sender.replaceTrack(pTrack);
    this.swapLocalVideo(pTrack);
    void this.applyQuality();
    this.bgMode = mode;
  }
  private swapLocalVideo(track: MediaStreamTrack): void {
    if (!this.local) return;
    this.local.getVideoTracks().forEach((t) => this.local?.removeTrack(t));
    this.local.addTrack(track);
    this.events.onLocalStream(this.local);
  }

  async setVideoQuality(q: VideoQuality): Promise<void> {
    this.quality = q;
    await this.applyQuality();
  }
  private async applyQuality(): Promise<void> {
    if (this.simulcast) return; // simulcast carries its own per-layer encodings; the manual picker doesn't apply
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === "video");
    if (!sender) return;
    const params = sender.getParameters();
    if (!params.encodings?.length) params.encodings = [{}];
    applyQualityPreset(params.encodings[0], this.quality);
    await sender.setParameters(params).catch(() => undefined);
  }

  // shareScreen sends the display as our video: swap the camera for the screen
  // (replaceTrack) or add it (auto-renegotiated to the SFU). "Stop sharing" auto-reverts.
  async shareScreen(): Promise<boolean> {
    if (!this.pc || !this.local || this.screenTrack) return false;
    const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
    const screen = display.getVideoTracks()[0];
    if (!screen) return false;
    this.screenTrack = screen;
    screen.onended = () => void this.stopScreen();
    const sender = this.pc.getSenders().find((s) => s.track?.kind === "video");
    if (sender?.track) {
      this.savedCamera = sender.track;
      await sender.replaceTrack(screen);
      this.local.removeTrack(this.savedCamera);
    } else {
      this.pc.addTrack(screen, this.local); // onnegotiationneeded re-offers
    }
    this.local.addTrack(screen);
    this.events.onLocalStream(this.local);
    void this.applyQuality();
    this.events.onScreenShare?.(true);
    return true;
  }

  async stopScreen(): Promise<void> {
    const screen = this.screenTrack;
    if (!this.pc || !this.local || !screen) return;
    this.screenTrack = null;
    const sender = this.pc.getSenders().find((s) => s.track === screen);
    this.local.removeTrack(screen);
    screen.stop();
    if (this.savedCamera && sender) {
      await sender.replaceTrack(this.savedCamera);
      this.local.addTrack(this.savedCamera);
      this.savedCamera = null;
    } else if (sender) {
      this.pc.removeTrack(sender); // onnegotiationneeded re-offers
    }
    this.events.onLocalStream(this.local);
    this.events.onScreenShare?.(false);
  }

  devices(): Promise<{ mics: MediaDeviceInfo[]; cameras: MediaDeviceInfo[] }> {
    return enumerateInputs();
  }
  setAudioInput(deviceId: string): Promise<void> {
    return this.switchTrack("audio", { deviceId: { exact: deviceId } });
  }
  setVideoInput(deviceId: string): Promise<void> {
    return this.switchTrack("video", { deviceId: { exact: deviceId } });
  }
  private facing: "user" | "environment" = "user";
  // flipCamera toggles front↔back by facingMode (the OS picks the right lens). See CallSession.
  async flipCamera(): Promise<void> {
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === "video");
    const current = (sender?.track?.getSettings().facingMode as "user" | "environment" | undefined) ?? this.facing;
    const next: "user" | "environment" = current === "environment" ? "user" : "environment";
    await this.switchTrack("video", { facingMode: { exact: next } });
    this.facing = next;
  }
  private async switchTrack(kind: "audio" | "video", constraint: MediaTrackConstraints): Promise<void> {
    if (!this.pc || !this.local) return;
    const oldTracks = kind === "audio" ? this.local.getAudioTracks() : this.local.getVideoTracks();
    const wasEnabled = oldTracks[0]?.enabled ?? true;
    const stream = await navigator.mediaDevices.getUserMedia(kind === "audio" ? { audio: constraint } : { video: constraint });
    const track = stream.getTracks()[0];
    if (!track) return;
    // Background effect on + switching CAMERA: feed the new camera to the effect (the sent
    // processed track is unchanged), so the blur survives the device switch.
    if (kind === "video" && this.bgEffect && this.rawCamera) {
      await this.bgEffect.setInput(new MediaStream([track]));
      this.rawCamera.stop();
      this.rawCamera = track;
      return;
    }
    track.enabled = wasEnabled;
    const sender = this.pc.getSenders().find((sd) => sd.track?.kind === kind);
    if (sender) await sender.replaceTrack(track);
    else this.pc.addTrack(track, this.local); // re-negotiates
    for (const t of oldTracks) {
      this.local.removeTrack(t);
      t.stop();
    }
    this.local.addTrack(track);
    this.events.onLocalStream(this.local);
    if (kind === "video") void this.applyQuality();
  }

  hangup(): void {
    if (!this.active) return;
    this.sendSignal(this.roomID, { kind: "leave" }); // tell the SFU to tear down our peer
    if (this.pc) {
      // Null the handlers before close() so the "closed" connectionstatechange can't re-enter hangup().
      this.pc.ontrack = this.pc.onicecandidate = this.pc.onnegotiationneeded = this.pc.onconnectionstatechange = null;
      this.pc.close();
    }
    this.pc = null;
    this.local?.getTracks().forEach((t) => t.stop());
    this.local = null;
    this.screenTrack?.stop();
    this.screenTrack = null;
    this.savedCamera?.stop();
    this.savedCamera = null;
    this.bgEffect?.dispose(); // close the MediaPipe segmenter (WASM/GPU), not just stop the loop
    this.bgEffect = null;
    this.rawCamera?.stop();
    this.rawCamera = null;
    this.bgMode = "none";
    this.videoTransceiver = null;
    this.active = false;
    this.roomID = "";
    this.events.onEnded();
  }
}
